import { TrendDirection, EmotionCategory } from '@shared/api/models/query/patient-daily-snapshot.model';

export interface WeeklyAdherenceChapters {
    scheduled: number;
    completed: number;
}

export interface WeeklyAdherenceCheckIns {
    scheduled: number;
    completed: number;
}

export interface CarerWeeklyAdherence {
    chapters: WeeklyAdherenceChapters;
    checkIns: WeeklyAdherenceCheckIns;
}

export interface WeeklySnapshot_Chapter {
    id?: number;
    code: string;
    title: string;
    progressValue: number;
    completed: boolean;
}

export interface WeeklySnapshot_MealPlanCheck {
    challenges: string[];
    wins: string[];
}

// ── Weekly snapshot v2 (UNIVAPLAT-2961 snapshot payload v2, PR #987) ───────────
// GET /api/v2/patients/:id/statistics/weekly/detail?dateStart=YYYYMMDD&type=patient|carer
// v2 rows carry payloadVersion: 2. Rows generated before the v2 backend landed have no
// payloadVersion key — the pre-v2 whole-snapshot types (the patient/carer snapshot
// interfaces, the code-keyed questionnaire-answer type, the whole-week behaviour-log
// entry type, the per-day check-in entry maps, and the old per-meal-total food-diary
// field names) were removed in sub-task 10 (UNIVAPLAT-3042): every consumer had
// migrated to the v2 shapes below,
// and a pre-regeneration row is now treated as "no v2 data" rather than parsed — see
// `isWeeklySnapshotV2` / `WeeklyDetailResult`. `WeeklySnapshot_Chapter` and
// `WeeklySnapshot_MealPlanCheck` keep their (legacy-looking) names because they are
// still live: the former is reused as-is by both v2 payloads' `chapters` field, and the
// latter is reused as `WeeklyMealPlanLegacy` for pre-2.9.0-app weeks embedded inside a
// v2 carer payload — an orthogonal, still-supported legacy axis, unrelated to
// `payloadVersion`. Contract pinned on the UNIVAPLAT-2962 "Frontend Implementation
// Plan" comment — never the Feature Spec sample.
//
// Note: this file intentionally does not import from the UNIVAPLAT-2360 (daily
// check-ins v2) branch, which is not yet on this Epic branch — `TrendDirection` above
// is reused as-is, and behaviour severity is typed locally (`WeeklyBehaviourSeverityV2`)
// rather than importing the not-yet-merged `BehaviourSeverity` from
// patient-statistics.model.ts.

export type WeeklyBehaviourSeverityV2 = 'grey' | 'green' | 'amber' | 'red';

/**
 * One reporter's (patient's or carer's) weekly figures for a single behaviour —
 * see {@link ObservedBehaviourV2}.
 */
export interface ObservedBehaviourSideV2 {
    value: number;
    /** Max severity band across the week's days (grey < green < amber < red). */
    severity: WeeklyBehaviourSeverityV2;
    /** Compares this week's max severity to last week's; null with no comparable data. */
    trend: TrendDirection | null;
    /**
     * `value` expressed as a percentage (0-100, already rounded/clamped server-side) of that
     * behaviour's WEEKLY cap — the same per-behaviour daily cap Daily uses, multiplied by 7
     * (UNIVAPLAT-3037 fix-up). Independent of `severity`, exactly as on Daily: a low percentage
     * can still carry a `red` severity when a single day this week crossed that behaviour's
     * clinical threshold.
     */
    percentage: number;
}

/**
 * UNIVAPLAT-3039 correction: this is NOT the flat `{name, value, severity, trend}` shape
 * originally pinned on the 2026-09-23 "Frontend Implementation Plan" comment (34348) and
 * used as such by sub-task 4 (UNIVAPLAT-3036)'s patient "Behaviours" panel. The actual
 * `buildBehaviourLog()` backend builder (api.cloud.univa.health,
 * `PatientWeeklyStatisticsSnapshotService`, UNIVAPLAT-2975 — decided via comments
 * 34116/34117 on UNIVAPLAT-2961, most recently touched by UNIVAPLAT-3146) always returns
 * BOTH sides per behaviour — `{name, patient: {...}, carer: {...}}` — for both the patient
 * and carer weekly payloads alike (same single builder call feeds
 * `PatientWeeklySnapshotV2.observedBehaviours` and `CarerWeeklySnapshotV2.observedBehaviours`).
 * Discovered while implementing UNIVAPLAT-3039 (carer "observed behaviours v2" panel) by
 * reading the backend source directly (`feature/UNIVAPLAT-2961-weekly-check-ins-v2-portal`,
 * commit 7aa0f3fc) — the ticket's own AC ("a patient value 3 and a carer value 2 ... shows 3
 * and 2 with their trend arrows") corroborates this shape and was the tell that the flat
 * type was wrong. Corrected here rather than left stale, since `weekly-observed-behaviours`
 * is shared by both the patient and carer pages and the flat shape meant the patient page's
 * panel was reading fields (`behaviour.value` etc.) the real API response never sent.
 */
export interface ObservedBehaviourV2 {
    name: string;
    patient: ObservedBehaviourSideV2;
    carer: ObservedBehaviourSideV2;
}

export interface WeeklyStressPeakV2 {
    value: number;
    weekday: string;
}

export interface WeeklyStressRollupV2 {
    /** Ymd-keyed map of this week's daily stress values; null = not rated that day. */
    values: { [ymd: string]: number | null };
    /** Absent when no day in the week was rated. */
    average?: number;
    /** Absent when no day in the week was rated. */
    peak?: WeeklyStressPeakV2;
}

export interface WeeklyMoodRollupV2 {
    answeredDays: number;
    mostCommon?: string;
    count?: number;
}

export interface WeeklySleepRollupV2 {
    tiredOrExhaustedDays: number;
    answeredDays: number;
}

/** Weekly daily-check-ins roll-up. Identical shape for patient and carer
 *  (UNIVAPLAT-2962 OQ-FE1, answered 2026-09-29): the carer payload is built on
 *  UNIVAPLAT-2961 from the same UDC-C roll-up logic as the patient side, replacing the
 *  legacy per-day SLE-C/YEM-C map. */
export interface WeeklyDailyCheckinsRollupV2 {
    stress: WeeklyStressRollupV2;
    mood: WeeklyMoodRollupV2;
    sleep: WeeklySleepRollupV2;
    edBehaviourCounter: number;
    /** UNIVAPLAT-3055 — count of days with a behaviour answer, so "0 events" from a week
     *  fully answered "None today" can be told apart from a week with no answers at all
     *  (UNIVAPLAT-2962 OQ-FE6). */
    edBehaviourAnsweredDays: number;
}

/** The "Severy" typo from the legacy payload is fixed on the v2 contract (UNIVAPLAT-2970). */
export type WeeklySeverityV2 = 'Subclinical' | 'Moderate' | 'Severe' | 'Extremely Severe';

export interface WeeklyEd15SubscaleV2 {
    score: number;
    severity: WeeklySeverityV2;
}

export interface WeeklyEd15BehaviourCountsV2 {
    /** null = Not applicable — never rendered or plotted as 0. */
    binge: number | null;
    vomited: number | null;
    laxatives: number | null;
    foodRestriction: number | null;
    exercise: number | null;
}

export interface WeeklyEd15AnswerV2 {
    value: number | null;
    trend: TrendDirection | null;
}

export interface WeeklyEd15SurveysV2 {
    overallScore: number;
    overallSeverity: WeeklySeverityV2;
    trend: TrendDirection | null;
    subscales: {
        weightShapeConcern: WeeklyEd15SubscaleV2;
        eatingConcern: WeeklyEd15SubscaleV2;
    };
    behaviourCounts: WeeklyEd15BehaviourCountsV2;
    /** 5 Ymd keys; null = no check-in that week. */
    history: { [ymd: string]: number | null };
    /** UNIVAPLAT-3054 — raw per-question answers, keys "1".."15", feeding the ED-15 detail
     *  pop-up (UNIVAPLAT-2962 OQ-FE4, answered 2026-09-24). null value = Not applicable. */
    answers: { [questionNumber: string]: WeeklyEd15AnswerV2 };
}

export interface WeeklyAdherenceFoodDiaryV2 {
    scheduled: number;
    completed: number;
}

export interface PatientWeeklyAdherenceV2 {
    chapters: WeeklyAdherenceChapters;
    checkIns: WeeklyAdherenceCheckIns;
    foodDiary: WeeklyAdherenceFoodDiaryV2;
}

export interface PatientWeeklySnapshotV2 {
    payloadVersion: 2;
    computedAt: string;
    /** UNIVAPLAT-3053 — 'ARFID' or null. No ARFID patient users exist today (OQ-G), so
     *  only the carer views currently branch on this, but it is typed on both payloads
     *  as pinned in the contract. */
    careProgramScope: string | null;
    adherence: PatientWeeklyAdherenceV2;
    dailyCheckins: WeeklyDailyCheckinsRollupV2;
    surveys: WeeklyEd15SurveysV2 | null;
    observedBehaviours: ObservedBehaviourV2[];
    chapters: WeeklySnapshot_Chapter[];
}

export interface WeeklyMealPlanItemV2 {
    label: string;
    note?: string | null;
}

export interface WeeklyMealPlanSideV2 {
    items: WeeklyMealPlanItemV2[];
    somethingElse?: string | null;
    nothingThisWeek: boolean;
}

export interface WeeklyMealPlanV2 {
    challenges: WeeklyMealPlanSideV2;
    wins: WeeklyMealPlanSideV2;
}

/** Pre-2.9.0 weeks: labels-only string arrays — same shape as the legacy
 *  WeeklySnapshot_MealPlanCheck above. */
export type WeeklyMealPlanLegacy = WeeklySnapshot_MealPlanCheck;

export type WeeklyDirection = 'better' | 'noChange' | 'worse';

export interface WeeklyObservationAreaV2 {
    direction: WeeklyDirection | null;
    /** 8 Ymd/week keys; the last one equals `direction`. */
    history: { [key: string]: WeeklyDirection | null };
}

export interface WeeklyObservationsV2 {
    eatingAtMealtimes: WeeklyObservationAreaV2;
    finishingPortions: WeeklyObservationAreaV2;
    varietyOfFoods: WeeklyObservationAreaV2;
    anxietyAroundFood: WeeklyObservationAreaV2;
    overallProgress: WeeklyObservationAreaV2;
}

export interface WeeklyHowItsGoingV2 {
    progressFeeling: 'notGood' | 'okay' | 'pleased' | null;
    univaHelpfulness: 'notVery' | 'aLittle' | 'very' | null;
}

export interface CarerWeeklySnapshotV2 {
    payloadVersion: 2;
    computedAt: string;
    careProgramScope: string | null;
    adherence: CarerWeeklyAdherence;
    mealPlan: WeeklyMealPlanV2 | WeeklyMealPlanLegacy | null;
    observations: WeeklyObservationsV2;
    howItsGoing: WeeklyHowItsGoingV2;
    /** Identical shape to the patient roll-up — see WeeklyDailyCheckinsRollupV2. */
    dailyCheckins: WeeklyDailyCheckinsRollupV2;
    observedBehaviours: ObservedBehaviourV2[];
    chapters: WeeklySnapshot_Chapter[];
    // No `surveys` key — the v2 carer payload drops the questionnaire entirely.
}

export type WeeklySnapshotV2 = PatientWeeklySnapshotV2 | CarerWeeklySnapshotV2;

/** No-snapshot payload — mirrors DailyNoSnapshotV2 from the UNIVAPLAT-2360 daily
 *  contract: the endpoint body is {"data": {"data": null, "noData": true}}, so after
 *  the service unwraps the outer envelope the caller receives this object. */
export interface WeeklyNoSnapshot {
    data: null;
    noData: true;
}

/** Unwrapped result of GET …/statistics/weekly/detail: the v2 payload, or a typed
 *  no-data marker. A pre-regeneration row (no `payloadVersion` key, old un-typed
 *  shape) is also a structural member of this union at runtime until the backend's
 *  historical regeneration command backfills it — the portal deliberately no longer
 *  types or parses that shape (UNIVAPLAT-3042). `isWeeklySnapshotV2` is the only guard
 *  callers need: "any payload that is not v2" (including a pre-regeneration row, and
 *  the explicit `WeeklyNoSnapshot` marker) is treated identically as "no v2 data" by
 *  every consumer — see the weekly detail pages' `loadSnapshot()`. */
export type WeeklyDetailResult = WeeklySnapshotV2 | WeeklyNoSnapshot;

export function isWeeklySnapshotV2(
    payload: WeeklyDetailResult | null | undefined
): payload is WeeklySnapshotV2 {
    return !!payload && 'payloadVersion' in payload && payload.payloadVersion === 2;
}

export function isMealPlanV2(
    mealPlan: WeeklyMealPlanV2 | WeeklyMealPlanLegacy | null | undefined
): mealPlan is WeeklyMealPlanV2 {
    return !!mealPlan && !Array.isArray((mealPlan as WeeklyMealPlanV2).challenges);
}
